/**
 * Team-template cards for the Project Setup agent: parse confirmation,
 * preview drawer, conversational column/row/bulk previews, export + apply.
 */
import {useEffect, useRef, useState} from 'react';
import {AlertTriangle, Download, FileSpreadsheet, Loader2, Table2, X} from 'lucide-react';

import {apiClient} from '@/shared/services/api-client';
import {Button} from '@/shared/ui/button';
import {Input} from '@/shared/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/shared/ui/sheet';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/ui/table';

export const PREVIEW_ROW_LIMIT = 20;
export const ROW_AUTO_CONFIRM_MS = 5000;

const SPREADSHEET_EXTS = new Set(['xlsx', 'xls', 'xlsm', 'csv', 'tsv']);
const AMBIGUOUS_EXTS = new Set(['docx', 'pdf', 'txt']);

export const extOf = (name = '') => {
  const m = /\.([a-z0-9]+)$/i.exec(String(name).trim());
  return m ? m[1].toLowerCase() : '';
};

export const preferTeamTemplate = (file: File) => SPREADSHEET_EXTS.has(extOf(file.name));
export const tryTeamTemplate = (file: File) =>
  preferTeamTemplate(file) || AMBIGUOUS_EXTS.has(extOf(file.name));

const draftRowCount = (draft: TeamTemplateDraft) =>
  Array.isArray(draft.rows) ? draft.rows.length : Number(draft.dataRowCount) || 0;

/** A roster file with exactly one data row uses the inline row preview, not bulk import. */
export const isSingleRowDraft = (draft: TeamTemplateDraft | null | undefined) =>
  Boolean(draft && draftRowCount(draft) === 1);

const rowWho = (row: Record<string, string>) => {
  const pick = (...keys: string[]) => {
    for (const k of keys) {
      const hit = Object.keys(row).find((h) => h.trim().toLowerCase() === k);
      const val = hit ? String(row[hit] ?? '').trim() : '';
      if (val) return val;
    }
    return '';
  };
  return pick('name', 'member', 'full name', 'email', 'e-mail') || 'this row';
};

const mappedFromRow = (row: Record<string, string>) => {
  const mapped: Record<string, string> = {};
  for (const [k, v] of Object.entries(row || {})) {
    const val = String(v ?? '').trim();
    if (k && val) mapped[k] = val;
  }
  return mapped;
};

/** Pending action that renders Confirm / Edit mapping / Cancel for an attached one-row file. */
export const rowPreviewFromDraft = (draft: TeamTemplateDraft): TemplatePendingAction | null => {
  if (!isSingleRowDraft(draft)) return null;
  const row = draft.rows[0] || {};
  const mapped = mappedFromRow(row);
  const who = rowWho(row);
  return {
    kind: 'team_template_row',
    action: 'add',
    autoConfirm: true,
    applyOnConfirm: true,
    label: `Add ${who} from ${draft.filename}`,
    mapped,
    unmapped: [],
    rowIndex: 0,
    nextTemplate: draft,
  };
};

export type TeamTemplateDraft = {
  filename: string;
  source?: string;
  fileType?: string;
  headers: string[];
  rows: Array<Record<string, string>>;
  columns?: Array<{id: string; label: string; type: string; options?: string[]}>;
  identityLabel?: string | null;
  identityHeaders?: string[];
  droppedMandatory?: string[];
  ignoredHeaders?: string[];
  dataRowCount?: number;
};

export type TemplatePendingAction = {
  kind: 'team' | 'team_column' | 'team_template_column' | 'team_template_row' | 'team_template_bulk';
  action?: string;
  label: string;
  autoConfirm?: boolean;
  name?: string;
  type?: string;
  options?: string[];
  position?: string;
  mapped?: Record<string, string>;
  unmapped?: string[];
  rowIndex?: number;
  valueKeys?: Record<string, string>;
  values?: Record<string, string>;
  email?: string;
  role?: string;
  names?: string[];
  team_ids?: string[];
  diff?: Array<{
    row: number;
    identity?: string;
    before: Record<string, string>;
    after: Record<string, string>;
  }>;
  rowCount?: number;
  nextTemplate?: TeamTemplateDraft;
  columns?: Array<{id?: string; label: string; type: string; options?: string[]}>;
  /** File-attach one-row preview: Confirm writes the row to the project. */
  applyOnConfirm?: boolean;
};

const IDENTITY_OR_MANDATORY_HEADERS = new Set([
  'email',
  'e-mail',
  'email address',
  'work email',
  'member email',
  'name',
  'full name',
  'member',
  'member name',
  'team member',
  'user',
  'role',
  'project role',
  'team role',
  'status',
  'join date',
  'join_date',
  'start date',
  'start',
]);

/** Avoid String(object) → "[object Object]" (Sonar typescript:S6551). */
const previewCellText = (raw: unknown): string => {
  if (raw == null) return '';
  if (typeof raw === 'string') return raw;
  if (typeof raw === 'number' || typeof raw === 'boolean' || typeof raw === 'bigint') {
    return String(raw);
  }
  if (typeof raw === 'object') {
    try {
      return JSON.stringify(raw);
    } catch {
      return '';
    }
  }
  return '';
};

const foldPreview = (s: unknown) =>
  previewCellText(s)
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();

const pickDraftCell = (row: Record<string, string>, names: string[]) => {
  const want = new Set(names.map((n) => n.toLowerCase()));
  for (const [key, raw] of Object.entries(row || {})) {
    if (!want.has(String(key).trim().toLowerCase())) continue;
    const val = String(raw ?? '').trim();
    if (val) return val;
  }
  return '';
};

const slugifyHeader = (label: string) =>
  String(label || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');

const metadataCell = (member: PreviewRosterMember, header: string) => {
  const meta = (member.team_metadata || {}) as Record<string, unknown>;
  const slug = slugifyHeader(header);
  const raw =
    meta[slug] ??
    meta[header] ??
    Object.entries(meta).find(
      ([k]) => k.toLowerCase() === slug || k.toLowerCase() === header.trim().toLowerCase(),
    )?.[1];
  return previewCellText(raw);
};

export type PreviewRosterMember = {
  name?: string;
  email?: string;
  role?: string;
  team_metadata?: Record<string, unknown> | null;
};

/** Match a template row to a live roster member (email, then unique name). */
export const matchDraftRowToMember = (
  row: Record<string, string>,
  members: PreviewRosterMember[],
) => {
  const email = pickDraftCell(row, [
    'email',
    'e-mail',
    'email address',
    'work email',
    'member email',
  ]).toLowerCase();
  const name = pickDraftCell(row, ['name', 'full name', 'member', 'member name', 'team member', 'user']);
  if (email) {
    const hit = members.find((m) => String(m.email || '').toLowerCase() === email);
    if (hit) return hit;
  }
  if (name) {
    const exact = members.filter((m) => foldPreview(m.name) === foldPreview(name));
    if (exact.length === 1) return exact[0];
  }
  return null;
};

export type TemplateApplyDiffRow = {
  row: number;
  identity: string;
  before: Record<string, string>;
  after: Record<string, string>;
};

const roleDisplayLabel = (stored: string) =>
  TEAM_ROLE_OPTIONS.find((o) => o.value === stored)?.label || stored;

/**
 * Before/after for template rows that match someone already on the team and
 * would change Role or a configurable cell. Name/email/status/join-date are
 * skipped — those are not written from this apply.
 */
export const buildTemplateApplyDiff = (
  draft: TeamTemplateDraft | null | undefined,
  members: PreviewRosterMember[] | null | undefined,
): TemplateApplyDiffRow[] => {
  if (!draft?.rows?.length || !members?.length) return [];
  const diffs: TemplateApplyDiffRow[] = [];
  draft.rows.forEach((row, i) => {
    const member = matchDraftRowToMember(row, members);
    if (!member) return;
    const before: Record<string, string> = {};
    const after: Record<string, string> = {};

    const incomingRole = normalizeTeamRoleValue(
      pickDraftCell(row, ['role', 'project role', 'team role']),
    );
    const currentRole = normalizeTeamRoleValue(member.role);
    // Participant is not a project team role — apply will not write it.
    if (incomingRole && incomingRole !== 'participant' && incomingRole !== currentRole) {
      before.Role = roleDisplayLabel(currentRole);
      after.Role = roleDisplayLabel(incomingRole);
    }

    for (const header of draft.headers || []) {
      if (!header || IDENTITY_OR_MANDATORY_HEADERS.has(header.trim().toLowerCase())) continue;
      const incoming = String(row[header] ?? '').trim();
      if (!incoming) continue;
      const current = metadataCell(member, header).trim();
      if (foldPreview(current) === foldPreview(incoming)) continue;
      before[header] = current;
      after[header] = incoming;
    }
    if (Object.keys(after).length) {
      diffs.push({
        row: i,
        identity: member.name || member.email || `Row ${i + 1}`,
        before,
        after,
      });
    }
  });
  return diffs;
};

const rosterFromTeamResponse = (payload: unknown): PreviewRosterMember[] => {
  const body = payload && typeof payload === 'object' ? (payload as {data?: unknown}) : null;
  const list = Array.isArray(body?.data) ? body.data : Array.isArray(payload) ? payload : [];
  return list.filter((m): m is PreviewRosterMember => Boolean(m) && typeof m === 'object');
};

export const fileTypeLabel = (draft: Pick<TeamTemplateDraft, 'filename' | 'fileType' | 'source'>) => {
  const raw = (draft.fileType || draft.source || extOf(draft.filename) || 'file').toUpperCase();
  if (raw === 'XLSX' || raw === 'XLS' || raw === 'XLSM') return 'XLSX';
  if (raw === 'SELECT') return 'XLSX';
  return raw;
};

const COLUMN_TYPES = [
  {value: 'text', label: 'Text'},
  {value: 'number', label: 'Number'},
  {value: 'date', label: 'Date'},
  {value: 'boolean', label: 'Boolean'},
  {value: 'select', label: 'Dropdown'},
  {value: 'percent', label: 'Percent'},
];

export const parseOptionList = (text = '', existing: string[] = []) => {
  const extra = String(text)
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  const out = [...existing];
  for (const v of extra) {
    if (!out.includes(v)) out.push(v);
  }
  return out;
};

export const applyColumnTypeToAction = (
  action: TemplatePendingAction,
  type: string,
  options: string[] = [],
): TemplatePendingAction => {
  const nextType = normalizeType(type);
  const nextOptions = nextType === 'select' ? options : [];
  const spoken = nextType === 'select' ? 'dropdown' : nextType;
  const at = action.position ? ` ${action.position}` : '';
  const withOptions = nextOptions.length ? ` with options: ${nextOptions.join(', ')}` : '';
  const label =
    action.action === 'retype'
      ? `Change the "${action.name}" column to ${spoken}${
          nextOptions.length ? ` (${nextOptions.join(', ')})` : ''
        }`
      : action.action === 'add'
        ? `Add the "${action.name}" column (${spoken})${at}${withOptions}`
        : action.label;
  const next: TemplatePendingAction = {...action, type: nextType, options: nextOptions, label};
  const name = String(action.name || '').trim().toLowerCase();
  if (name && Array.isArray(action.columns)) {
    next.columns = action.columns.map((c) => {
      const labelMatch = String(c.label || '').toLowerCase() === name;
      const isAdd = action.action === 'add' && labelMatch;
      const isRetype = action.action === 'retype' && labelMatch;
      if (!isAdd && !isRetype) return c;
      return {...c, type: nextType, options: nextOptions};
    });
  }
  return next;
};

export const TEAM_ROLE_OPTIONS = [
  {value: 'project_manager', label: 'PM'},
  {value: 'moderator', label: 'Moderator'},
  {value: 'scheduler', label: 'Scheduler'},
];

const TEMPLATE_ROLE_OPTIONS = [
  ...TEAM_ROLE_OPTIONS,
  {value: 'participant', label: 'Participant'},
];

const TEAM_STATUS_OPTIONS = [
  {value: 'active', label: 'Active'},
  {value: 'invited', label: 'Invited'},
  {value: 'inactive', label: 'Inactive'},
];

const BOOLEAN_OPTIONS = [
  {value: 'true', label: 'Yes'},
  {value: 'false', label: 'No'},
];

export const normalizeTeamRoleValue = (raw = '') => {
  const r = String(raw).trim().toLowerCase().replace(/\s+/g, '_');
  if (['project_manager', 'pm', 'projectmanager', 'project_manager'].includes(r)) {
    return 'project_manager';
  }
  if (r === 'moderator') return 'moderator';
  if (r === 'scheduler') return 'scheduler';
  if (r === 'participant') return 'participant';
  return '';
};

const isRoleField = (label: string) => /^(role|team role)$/i.test(label.trim());
const isStatusField = (label: string) => /^status$/i.test(label.trim());

export const choicesForMappedField = (
  action: Pick<TemplatePendingAction, 'kind' | 'action' | 'nextTemplate'>,
  label: string,
): Array<{value: string; label: string}> => {
  if (isRoleField(label)) {
    return action.kind === 'team_template_row' ? TEMPLATE_ROLE_OPTIONS : TEAM_ROLE_OPTIONS;
  }
  if (isStatusField(label)) return TEAM_STATUS_OPTIONS;
  const col = action.nextTemplate?.columns?.find((c) => c.label.toLowerCase() === label.toLowerCase());
  if (!col) return [];
  if ((col.type === 'select' || col.type === 'enum') && col.options?.length) {
    return col.options.map((o) => ({value: o, label: o}));
  }
  if (col.type === 'boolean') return BOOLEAN_OPTIONS;
  return [];
};

const displayMappedValue = (
  action: Pick<TemplatePendingAction, 'kind' | 'action' | 'nextTemplate'>,
  label: string,
  value: string,
) => {
  const choices = choicesForMappedField(action, label);
  const match = choices.find((c) => c.value === value || c.label.toLowerCase() === String(value).toLowerCase());
  return match?.label ?? value;
};

const initialMappedEdits = (action: TemplatePendingAction): Record<string, string> => {
  const mapped = {...action.mapped};
  if (!Object.keys(mapped).length) {
    if (action.name) mapped.Name = String(action.name);
    if (action.email) mapped.Email = String(action.email);
    if (action.role) mapped.Role = String(action.role);
  }
  for (const [key, raw] of Object.entries(mapped)) {
    const choices = choicesForMappedField(action, key);
    if (!choices.length) continue;
    const hit =
      choices.find((c) => c.value === raw) ||
      choices.find((c) => c.label.toLowerCase() === String(raw).toLowerCase()) ||
      choices.find((c) => c.value === normalizeTeamRoleValue(raw));
    if (hit) mapped[key] = hit.value;
  }
  return mapped;
};

export const normalizeType = (raw?: string) => {
  const t = String(raw || 'text').toLowerCase();
  if (t === 'dropdown' || t === 'enum') return 'select';
  return t;
};

export const TemplateDetectedCard = ({
  draft,
  summary,
  applying = false,
  onPreview,
  onConfirm,
  onCancel,
}: {
  draft: TeamTemplateDraft;
  summary: string;
  applying?: boolean;
  onPreview: () => void;
  onConfirm: () => void;
  onCancel: () => void;
}) => (
  <div className="ml-10 rounded-lg border border-sky-300 bg-sky-50 p-3 dark:border-sky-500/30 dark:bg-sky-500/10">
    <div className="flex items-center gap-2 text-sm font-medium text-sky-950 dark:text-sky-200">
      <FileSpreadsheet className="h-4 w-4" />
      Template detected
    </div>
    <p className="mt-1 text-sm text-sky-950 dark:text-sky-100">
      <span className="font-medium">{draft.filename}</span>
      <span className="text-muted-foreground"> · {fileTypeLabel(draft)}</span>
    </p>
    <p className="mt-1 text-sm text-sky-900 dark:text-sky-200/90">{summary}</p>
    <div className="mt-2.5 flex flex-wrap gap-2">
      <Button size="sm" variant="outline" onClick={onPreview} disabled={applying}>
        <Table2 className="mr-1.5 h-3.5 w-3.5" />
        Preview table
      </Button>
      <Button size="sm" onClick={onConfirm} disabled={applying}>
        {applying ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null}
        Confirm import
      </Button>
      <Button size="sm" variant="ghost" onClick={onCancel} disabled={applying}>
        Cancel
      </Button>
    </div>
  </div>
);

export const TemplateErrorCard = ({
  reason,
  onRetry,
}: {
  reason: string;
  onRetry: () => void;
}) => (
  <div className="ml-10 rounded-lg border border-red-300 bg-red-50 p-3 dark:border-red-500/30 dark:bg-red-500/10">
    <div className="flex items-center gap-2 text-sm font-medium text-red-900 dark:text-red-200">
      <AlertTriangle className="h-4 w-4" />
      Could not read that template
    </div>
    <p className="mt-1 whitespace-pre-line text-sm text-red-900 dark:text-red-200/90">{reason}</p>
    <Button size="sm" className="mt-2.5" variant="outline" onClick={onRetry}>
      Upload another file
    </Button>
  </div>
);

export const TemplatePreviewSheet = ({
  open,
  onOpenChange,
  draft,
  projectId,
  applying,
  onApply,
  onExport,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  draft: TeamTemplateDraft | null;
  projectId: string;
  applying: boolean;
  onApply: () => void;
  onExport: (format: 'xlsx' | 'csv') => void;
}) => {
  const [members, setMembers] = useState<PreviewRosterMember[]>([]);

  useEffect(() => {
    if (!open || !projectId) return undefined;
    let cancelled = false;
    apiClient
      .get(`/team/project/${encodeURIComponent(projectId)}`)
      .then((res) => {
        if (!cancelled) setMembers(rosterFromTeamResponse(res.data));
      })
      .catch(() => {
        if (!cancelled) setMembers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, projectId, draft?.filename]);

  if (!draft) return null;
  const rows = (draft.rows || []).slice(0, PREVIEW_ROW_LIMIT);
  const total = draft.rows?.length ?? draft.dataRowCount ?? 0;
  const diff = buildTemplateApplyDiff(draft, members);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col sm:max-w-3xl">
        <SheetHeader>
          <SheetTitle>Team template preview</SheetTitle>
          <SheetDescription>
            {draft.filename} · {fileTypeLabel(draft)} · {draft.headers.length} columns · {total} rows
            {total > PREVIEW_ROW_LIMIT ? ` (showing first ${PREVIEW_ROW_LIMIT})` : ''}
          </SheetDescription>
          <Button
            type="button"
            variant="link"
            size="sm"
            className="h-auto justify-start px-0"
            disabled={!projectId}
            onClick={() => onExport('xlsx')}
          >
            <Download className="mr-1 h-3.5 w-3.5" />
            Download current template
          </Button>
        </SheetHeader>
        <div className="min-h-0 flex-1 overflow-auto">
          {diff.length > 0 ? (
            <div
              className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-3 dark:border-amber-500/30 dark:bg-amber-500/10"
              data-testid="template-preview-diff"
            >
              <div className="text-sm font-medium text-amber-900 dark:text-amber-300">
                Changes vs current roster
              </div>
              <p className="mt-1 text-sm text-amber-900 dark:text-amber-200/90">
                {`${diff.length} row${diff.length === 1 ? '' : 's'} will change`}
              </p>
              <ul
                className="mt-2 max-h-64 min-h-0 space-y-1 overflow-y-auto overscroll-contain pr-1 text-xs"
                data-testid="bulk-change-diff"
              >
                {diff.map((d) => (
                  <li key={`${d.row}-${d.identity}`}>
                    <span className="font-medium">{d.identity}: </span>
                    {Object.keys(d.after).map((k, i) => (
                      <span key={k}>
                        {i > 0 ? '; ' : ''}
                        {k} “{d.before[k] || '∅'}” → “{d.after[k]}”
                      </span>
                    ))}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          <Table>
            <TableHeader>
              <TableRow>
                {draft.headers.map((h) => (
                  <TableHead key={h}>{h}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={Math.max(draft.headers.length, 1)} className="text-muted-foreground">
                    No data rows yet.
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row, i) => (
                  <TableRow key={`${i}-${row[draft.identityLabel || draft.headers[0]] || i}`}>
                    {draft.headers.map((h) => (
                      <TableCell key={h}>{row[h] || ''}</TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
        <SheetFooter className="mt-4 gap-2 sm:justify-start">
          <Button
            variant="outline"
            size="sm"
            disabled={!projectId}
            onClick={() => onExport('xlsx')}
          >
            <Download className="mr-1.5 h-3.5 w-3.5" />
            Export XLSX
          </Button>
          <Button variant="outline" size="sm" disabled={!projectId} onClick={() => onExport('csv')}>
            <Download className="mr-1.5 h-3.5 w-3.5" />
            Export CSV
          </Button>
          <Button size="sm" disabled={!projectId || applying} onClick={() => onApply()}>
            {applying ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null}
            Apply to project team roster
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export const TemplateColumnCard = ({
  action,
  busy,
  onApply,
  onCancel,
  title = 'Change preview',
  applyLabel = 'Apply',
}: {
  action: TemplatePendingAction;
  busy: boolean;
  onApply: (next: TemplatePendingAction) => void;
  onCancel: () => void;
  title?: string;
  applyLabel?: string;
}) => {
  const [type, setType] = useState(normalizeType(action.type));
  const [optionText, setOptionText] = useState('');
  const [options, setOptions] = useState<string[]>(action.options || []);
  const canEditType = action.action === 'add' || action.action === 'retype';

  const addOption = () => {
    const next = parseOptionList(optionText, options);
    if (next.length === options.length) return;
    setOptions(next);
    setOptionText('');
  };

  const confirm = () => {
    const nextOptions = type === 'select' ? parseOptionList(optionText, options) : options;
    onApply(applyColumnTypeToAction(action, type, nextOptions));
  };

  return (
    <div className="ml-10 rounded-lg border border-amber-300 bg-amber-50 p-3 dark:border-amber-500/30 dark:bg-amber-500/10">
      <div className="text-sm font-medium text-amber-900 dark:text-amber-300">{title}</div>
      <dl className="mt-2 grid gap-1 text-sm text-amber-950 dark:text-amber-100">
        <div>
          <dt className="inline text-muted-foreground">Change type: </dt>
          <dd className="inline font-medium">
            {action.action === 'remove'
              ? 'Remove column'
              : action.action === 'retype'
                ? 'Change column type'
                : 'Add column'}
          </dd>
        </div>
        {action.position ? (
          <div>
            <dt className="inline text-muted-foreground">Position: </dt>
            <dd className="inline">{action.position}</dd>
          </div>
        ) : null}
        <div>
          <dt className="inline text-muted-foreground">Name: </dt>
          <dd className="inline">{action.name}</dd>
        </div>
      </dl>
      {canEditType ? (
        <div className="mt-2 space-y-2">
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="h-8 w-40 bg-white" aria-label="Column type">
              <SelectValue placeholder="Column type" />
            </SelectTrigger>
            <SelectContent>
              {COLUMN_TYPES.map((t) => (
                <SelectItem key={t.value} value={t.value}>
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {type === 'select' ? (
            <div>
              <div className="flex flex-wrap gap-1">
                {options.map((o) => (
                  <span
                    key={o}
                    className="inline-flex items-center gap-1 rounded-full bg-white px-2 py-0.5 text-xs ring-1 ring-border"
                  >
                    {o}
                    <button
                      type="button"
                      aria-label={`Remove ${o}`}
                      onClick={() => setOptions((prev) => prev.filter((x) => x !== o))}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
              <Input
                className="mt-1 h-8 bg-white"
                placeholder="Add dropdown value and press Enter"
                value={optionText}
                onChange={(e) => setOptionText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addOption();
                  }
                }}
              />
            </div>
          ) : null}
        </div>
      ) : null}
      <p className="mt-2 text-sm text-amber-900 dark:text-amber-200/90">
        {applyColumnTypeToAction(action, type, parseOptionList(optionText, options)).label}
      </p>
      <div className="mt-2.5 flex gap-2">
        <Button
          size="sm"
          disabled={busy}
          onClick={confirm}
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : applyLabel}
        </Button>
        <Button size="sm" variant="outline" disabled={busy} onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
};

const mappedRowIndex = (action: TemplatePendingAction) => {
  if (typeof action.rowIndex === 'number') return action.rowIndex;
  if (action.action === 'add') return (action.nextTemplate?.rows?.length ?? 1) - 1;
  return -1;
};

const withMappedTemplateRow = (
  draft: TeamTemplateDraft,
  idx: number,
  mapped: Record<string, string>,
): TeamTemplateDraft => ({
  ...draft,
  rows: (draft.rows ?? []).map((row, i) => (i === idx ? {...row, ...mapped} : row)),
});

const withMappedIdentity = (next: TemplatePendingAction, mapped: Record<string, string>) => {
  if (mapped.Name != null) next.name = mapped.Name;
  if (mapped.Email != null) next.email = mapped.Email;
  if (mapped.Role != null) next.role = mapped.Role;
};

const withMappedFieldValues = (action: TemplatePendingAction, mapped: Record<string, string>) => {
  const keys = action.valueKeys;
  if (!keys) return action.values;
  const values = {...action.values};
  for (const [label, value] of Object.entries(mapped)) {
    const id = keys[label];
    if (id) values[id] = value;
  }
  return values;
};

export const applyRowMapping = (
  action: TemplatePendingAction,
  mapped: Record<string, string>,
): TemplatePendingAction => {
  const next: TemplatePendingAction = {...action, mapped};
  if (action.nextTemplate) {
    const idx = mappedRowIndex(action);
    if (idx >= 0) next.nextTemplate = withMappedTemplateRow(action.nextTemplate, idx, mapped);
  }
  if (action.action === 'add') withMappedIdentity(next, mapped);
  if (action.action === 'update_fields') next.values = withMappedFieldValues(action, mapped);
  return next;
};

/** Live multi-member field update, or a template bulk action — not a single-row preview. */
export const isBulkPreview = (a: {
  kind?: string;
  action?: string;
  team_ids?: unknown;
  rowCount?: number;
  diff?: unknown[];
}): boolean => {
  if (a.kind === 'team_template_bulk') return true;
  if (a.action !== 'update_fields') return false;
  if (Array.isArray(a.team_ids) && a.team_ids.length > 1) return true;
  if (typeof a.rowCount === 'number' && a.rowCount > 1) return true;
  return Array.isArray(a.diff) && a.diff.length > 1;
};

const bulkPreviewDiff = (action: TemplatePendingAction) => {
  if (action.diff?.length) return action.diff;
  const names = action.names || [];
  const after = action.mapped || {};
  const keys = Object.keys(after);
  if (!names.length || !keys.length) return [];
  return names.map((identity, row) => ({
    row,
    identity,
    before: Object.fromEntries(keys.map((k) => [k, ''])),
    after,
  }));
};

const bulkPreviewCount = (action: TemplatePendingAction) => {
  if (typeof action.rowCount === 'number') return action.rowCount;
  const n = bulkPreviewDiff(action).length;
  if (n) return n;
  return Array.isArray(action.team_ids) ? action.team_ids.length : 0;
};

const MappedFieldEditor = ({
  action,
  field,
  value,
  onChange,
}: {
  action: TemplatePendingAction;
  field: string;
  value: string;
  onChange: (value: string) => void;
}) => {
  const choices = choicesForMappedField(action, field);
  if (choices.length) {
    return (
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-8 bg-white" aria-label={field}>
          <SelectValue placeholder={`Select ${field}`} />
        </SelectTrigger>
        <SelectContent>
          {choices.map((c) => (
            <SelectItem key={c.value} value={c.value}>
              {c.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }
  return (
    <Input
      className="h-8 bg-white"
      aria-label={field}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  );
};

export const TemplateRowCard = ({
  action,
  busy,
  onConfirm,
  onCancel,
}: {
  action: TemplatePendingAction;
  busy: boolean;
  onConfirm: (next?: TemplatePendingAction) => void;
  onCancel: () => void;
}) => {
  const [editing, setEditing] = useState(false);
  const [edits, setEdits] = useState<Record<string, string>>(() => initialMappedEdits(action));
  const fields = Object.keys(edits);
  const isEdit = action.action === 'edit' || action.action === 'update_fields';
  const confirmRef = useRef(onConfirm);
  confirmRef.current = onConfirm;
  const editsRef = useRef(edits);
  editsRef.current = edits;

  useEffect(() => {
    if (!action.autoConfirm || busy || editing) return undefined;
    const t = window.setTimeout(
      () => confirmRef.current(applyRowMapping(action, editsRef.current)),
      ROW_AUTO_CONFIRM_MS,
    );
    return () => window.clearTimeout(t);
  }, [action, action.autoConfirm, busy, editing]);

  return (
    <div className="ml-10 rounded-lg border border-amber-300 bg-amber-50 p-3 dark:border-amber-500/30 dark:bg-amber-500/10">
      <div className="text-sm font-medium text-amber-900 dark:text-amber-300">
        {isEdit ? 'Row update preview' : 'Row preview'}
      </div>
      <p className="mt-1 text-sm text-amber-900 dark:text-amber-200/90">{action.label}</p>
      {fields.length ? (
        <dl className="mt-2 space-y-1 text-sm">
          {fields.map((k) => (
            <div key={k} className="flex items-center gap-2">
              <dt className="w-32 shrink-0 text-muted-foreground">{k}</dt>
              <dd className="min-w-0 flex-1">
                {editing ? (
                  <MappedFieldEditor
                    action={action}
                    field={k}
                    value={edits[k] ?? ''}
                    onChange={(v) => setEdits((prev) => ({...prev, [k]: v}))}
                  />
                ) : (
                  <span className="rounded bg-amber-100 px-1 font-medium dark:bg-amber-500/20">
                    {displayMappedValue(action, k, edits[k] ?? '')}
                  </span>
                )}
              </dd>
            </div>
          ))}
        </dl>
      ) : null}
      {action.autoConfirm && !editing ? (
        <p className="mt-2 text-xs text-muted-foreground">Auto-confirms in 5 seconds if you take no action.</p>
      ) : null}
      <div className="mt-2.5 flex flex-wrap gap-2">
        <Button size="sm" disabled={busy} onClick={() => onConfirm(applyRowMapping(action, edits))}>
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Confirm'}
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={busy}
          onClick={() => setEditing((on) => !on)}
        >
          {editing ? 'Done editing' : 'Edit mapping'}
        </Button>
        <Button size="sm" variant="outline" disabled={busy} onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
};

export const TemplateBulkCard = ({
  action,
  busy,
  onConfirm,
  onCancel,
}: {
  action: TemplatePendingAction;
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) => {
  const diff = bulkPreviewDiff(action);
  const n = bulkPreviewCount(action);
  return (
  <div
    className="ml-10 rounded-lg border border-amber-300 bg-amber-50 p-3 dark:border-amber-500/30 dark:bg-amber-500/10"
    data-testid="bulk-change-preview"
  >
    <div className="text-sm font-medium text-amber-900 dark:text-amber-300">Bulk change preview</div>
    {action.label ? (
      <p className="mt-1 text-sm text-amber-900 dark:text-amber-200/90">{action.label}</p>
    ) : null}
    <p className="mt-1 text-sm text-amber-900 dark:text-amber-200/90">
      {`${n} row${n === 1 ? '' : 's'} will change`}
    </p>
    {diff.length ? (
      <ul
        className="mt-2 max-h-64 min-h-0 space-y-1 overflow-y-auto overscroll-contain pr-1 text-xs"
        data-testid="bulk-change-diff"
      >
        {diff.map((d) => (
          <li key={`${d.row}-${d.identity}`}>
            <span className="font-medium">{d.identity || `Row ${d.row + 1}`}: </span>
            {Object.keys(d.after).map((k) => (
              <span key={k}>
                {k} “{d.before[k] || '∅'}” → “{d.after[k]}”
              </span>
            ))}
          </li>
        ))}
      </ul>
    ) : null}
    <div className="mt-2.5 flex shrink-0 flex-wrap gap-2">
      <Button size="sm" disabled={busy} onClick={onConfirm}>
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Confirm bulk change'}
      </Button>
      <Button size="sm" variant="outline" disabled={busy} onClick={onCancel}>
        Cancel
      </Button>
    </div>
  </div>
  );
};

export const downloadTemplateExport = async (
  projectId: string,
  draft: TeamTemplateDraft,
  format: 'xlsx' | 'csv',
) => {
  const res = await apiClient.post(
    `/agent-setup/${encodeURIComponent(projectId)}/team-template/export`,
    {template: draft, format},
    {responseType: 'blob'},
  );
  const blob = res.data as Blob;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const base = draft.filename.replace(/\.[a-z0-9]+$/i, '');
  a.href = url;
  a.download = `${base}.${format}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};
